import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import companiesService, { Company } from "../../features/companies/companiesService";
import bmsService from "../../features/bms/bmsService";

interface SimpleChatbot {
  id: number;
  name: string;
  company_id: number;
}

interface BmsChatbotSelectorProps {
  chatbotId: number | null;
  onChatbotChange: (chatbotId: number | null) => void;
}

interface SelectOption {
  value: number;
  label: string;
}

const STORAGE_KEYS = {
  companyId: "bms:selectedCompanyId",
  chatbotId: "bms:selectedChatbotId",
};

const BmsChatbotSelector: React.FC<BmsChatbotSelectorProps> = ({
  chatbotId,
  onChatbotChange,
}) => {
  const { t } = useTranslation();
  const user = useSelector((s: RootState) => s.auth.user);

  const isGlobalAdmin = useMemo(
    () => user?.roles?.some((r) => r === "superadmin" || r === "admin") ?? false,
    [user]
  );

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [chatbots, setChatbots] = useState<SimpleChatbot[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isLoadingChatbots, setIsLoadingChatbots] = useState(false);

  const persistSelections = useCallback((companyId: number | null, selectedChatbotId: number | null) => {
    if (companyId == null) {
      localStorage.removeItem(STORAGE_KEYS.companyId);
    } else {
      localStorage.setItem(STORAGE_KEYS.companyId, String(companyId));
    }

    if (selectedChatbotId == null) {
      localStorage.removeItem(STORAGE_KEYS.chatbotId);
    } else {
      localStorage.setItem(STORAGE_KEYS.chatbotId, String(selectedChatbotId));
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    setIsLoadingCompanies(true);
    try {
      if (isGlobalAdmin) {
        const res = await companiesService.getCompanies({ limit: 500 });
        setCompanies(res.companies || []);
      } else {
        const userCompanies = (user?.companies || []).map((uc) => ({
          id: uc.company_id,
          company_name: uc.company_name,
        })) as Company[];
        setCompanies(userCompanies);
      }
    } catch {
      setCompanies([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  }, [isGlobalAdmin, user]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (companies.length === 0 || selectedCompanyId !== null) {
      return;
    }

    const storedCompanyIdRaw = localStorage.getItem(STORAGE_KEYS.companyId);
    const storedCompanyId = storedCompanyIdRaw ? Number(storedCompanyIdRaw) : null;
    const hasStoredCompany = storedCompanyId !== null && companies.some((c) => c.id === storedCompanyId);
    setSelectedCompanyId(hasStoredCompany ? storedCompanyId : companies[0].id);
  }, [companies, selectedCompanyId]);

  const loadChatbots = useCallback(async () => {
    if (selectedCompanyId === null) {
      setChatbots([]);
      return;
    }

    setIsLoadingChatbots(true);
    try {
      const res = await bmsService.listBmsEnabledChatbots({ company_id: selectedCompanyId });
      const list: SimpleChatbot[] = (res.data || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        company_id: c.company_id,
      }));
      setChatbots(list);

      if (list.length > 0) {
        const storedChatbotIdRaw = localStorage.getItem(STORAGE_KEYS.chatbotId);
        const storedChatbotId = storedChatbotIdRaw ? Number(storedChatbotIdRaw) : null;
        const currentStillExists = chatbotId !== null && list.some((c) => c.id === chatbotId);
        const storedStillExists = storedChatbotId !== null && list.some((c) => c.id === storedChatbotId);

        if (currentStillExists && chatbotId !== null) {
          persistSelections(selectedCompanyId, chatbotId);
          return;
        }

        const fallbackChatbotId = storedStillExists ? storedChatbotId : list[0].id;
        onChatbotChange(fallbackChatbotId);
        persistSelections(selectedCompanyId, fallbackChatbotId);
      } else {
        onChatbotChange(null);
        persistSelections(selectedCompanyId, null);
      }
    } catch {
      setChatbots([]);
      onChatbotChange(null);
      persistSelections(selectedCompanyId, null);
    } finally {
      setIsLoadingChatbots(false);
    }
  }, [chatbotId, onChatbotChange, persistSelections, selectedCompanyId]);

  useEffect(() => {
    loadChatbots();
  }, [loadChatbots]);

  const handleCompanyChange = (companyId: number) => {
    setSelectedCompanyId(companyId);
    onChatbotChange(null);
    persistSelections(companyId, null);
  };

  const handleChatbotChange = (selectedOption: SelectOption | null) => {
    const selectedChatbotId = selectedOption?.value ?? null;
    onChatbotChange(selectedChatbotId);
    persistSelections(selectedCompanyId, selectedChatbotId);
  };

  const showCompanySelector = isGlobalAdmin || companies.length > 1;
  const companyOptions = useMemo<SelectOption[]>(
    () => companies.map((c) => ({ value: c.id, label: c.company_name })),
    [companies]
  );
  const chatbotOptions = useMemo<SelectOption[]>(
    () => chatbots.map((c) => ({ value: c.id, label: c.name })),
    [chatbots]
  );
  const selectedCompanyOption = useMemo(
    () => companyOptions.find((opt) => opt.value === selectedCompanyId) ?? null,
    [companyOptions, selectedCompanyId]
  );
  const selectedChatbotOption = useMemo(
    () => chatbotOptions.find((opt) => opt.value === chatbotId) ?? null,
    [chatbotId, chatbotOptions]
  );

  return (
    <Row className="g-2 mb-3 bms-chatbot-selector-row">
      {showCompanySelector && (
        <Col md={6} className="bms-chatbot-selector-col">
          <Select
            classNamePrefix="bms-rs"
            options={companyOptions}
            value={selectedCompanyOption}
            onChange={(option) => {
              if (option?.value) {
                handleCompanyChange(option.value);
              }
            }}
            isDisabled={isLoadingCompanies}
            isSearchable
            isClearable={false}
            placeholder={t("bms_select_company")}
            noOptionsMessage={() => t("bms_no_data")}
          />
        </Col>
      )}
      <Col md={showCompanySelector ? 6 : 12} className="bms-chatbot-selector-col">
        <Select
          classNamePrefix="bms-rs"
          options={chatbotOptions}
          value={selectedChatbotOption}
          onChange={(option) => handleChatbotChange(option as SelectOption | null)}
          isSearchable
          isClearable={false}
          isDisabled={isLoadingChatbots || chatbots.length === 0}
          placeholder={t("bms_select_chatbot")}
          noOptionsMessage={() => (
            isLoadingChatbots
              ? t("loading")
              : selectedCompanyId
                ? t("bms_no_chatbots_for_company")
                : t("bms_select_company_first")
          )}
        />
      </Col>
    </Row>
  );
};

export default BmsChatbotSelector;
